import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { PrototypeIcon } from './PrototypeIcon';

export interface HostingTechnologyOption {
  readonly value: string;
  readonly name: string;
  readonly logo: string;
}
/** Liste bornée, sans saisie libre. Focus et sélection restent indépendants. */
export function HostingTechnologySelect({
  label,
  options,
  values,
  multiple = false,
  onChange,
}: {
  readonly label: string;
  readonly options: readonly HostingTechnologyOption[];
  readonly values: readonly string[];
  readonly multiple?: boolean;
  readonly onChange: (values: readonly string[]) => void;
}): React.JSX.Element {
  const id = useId(),
    root = useRef<HTMLDivElement>(null),
    trigger = useRef<HTMLButtonElement>(null),
    list = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false),
    [active, setActive] = useState(0);
  const typed = useRef({ value: '', time: 0 });
  const choices = [{ value: '', name: 'Autre/Inconnu', logo: '' }, ...options];
  const selected = choices.filter((option) =>
    option.value === '' ? values.length === 0 : values.includes(option.value),
  );
  useEffect(() => {
    if (open) list.current?.querySelectorAll<HTMLElement>('[role="option"]')[active]?.focus();
  }, [open, active]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent): void => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  function close(): void {
    setOpen(false);
    trigger.current?.focus();
  }
  function show(last = false): void {
    if (trigger.current?.matches(':disabled')) return;
    typed.current = { value: '', time: 0 };
    const index = choices.findIndex((option) =>
      option.value === '' ? values.length === 0 : values.includes(option.value),
    );
    setActive(last ? choices.length - 1 : Math.max(0, index));
    setOpen(true);
  }
  function choose(value: string): void {
    if (trigger.current?.matches(':disabled')) return;
    if (value === '') {
      onChange([]);
      close();
      return;
    }
    if (!multiple) {
      onChange([value]);
      close();
      return;
    }
    onChange(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
  }
  function navigate(event: KeyboardEvent<HTMLLIElement>, index: number): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (event.key === 'Tab') {
      setOpen(false);
      trigger.current?.focus();
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      choose(choices[index]!.value);
      return;
    }
    const next =
      event.key === 'ArrowDown'
        ? Math.min(choices.length - 1, index + 1)
        : event.key === 'ArrowUp'
          ? Math.max(0, index - 1)
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? choices.length - 1
              : null;
    if (next !== null) {
      event.preventDefault();
      setActive(next);
      return;
    }
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const now = Date.now(),
        text = fold(event.key);
      typed.current = {
        value: now - typed.current.time > 700 ? text : typed.current.value + text,
        time: now,
      };
      const start = (index + 1) % choices.length;
      const found = Array.from(
        { length: choices.length },
        (_, offset) => (start + offset) % choices.length,
      ).find((position) => fold(choices[position]!.name).startsWith(typed.current.value));
      if (found !== undefined) {
        event.preventDefault();
        setActive(found);
      }
    }
  }
  return (
    <div
      className="hosting-technology-select"
      ref={root}
      onBlur={(event) => {
        if (
          !(event.relatedTarget instanceof Node) ||
          !event.currentTarget.contains(event.relatedTarget)
        )
          setOpen(false);
      }}
    >
      <span id={id + '-label'} className="hosting-technology-select__label">
        {label}
      </span>
      <button
        ref={trigger}
        type="button"
        className="hosting-technology-select__trigger"
        aria-labelledby={id + '-label ' + id + '-value'}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={id + '-list'}
        onClick={() => (open ? close() : show())}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            show(event.key === 'ArrowUp');
          }
          if (event.key === 'Escape' && open) {
            event.preventDefault();
            event.stopPropagation();
            close();
          }
        }}
      >
        <span id={id + '-value'} className="hosting-technology-select__value">
          {selected.map((option, index) => (
            <span key={option.value} className="hosting-technology-select__choice">
              <span className="hosting-technology-select__logo">
                {option.logo ? (
                  <img src={option.logo} alt="" />
                ) : (
                  <PrototypeIcon name="circle-help" />
                )}
              </span>
              <span>{option.name + (index < selected.length - 1 ? ', ' : '')}</span>
            </span>
          ))}
        </span>
        <PrototypeIcon name="chevron-down" />
      </button>
      <ul
        ref={list}
        id={id + '-list'}
        role="listbox"
        aria-labelledby={id + '-label'}
        aria-multiselectable={multiple ? true : undefined}
        className="hosting-technology-select__list"
        hidden={!open}
      >
        {choices.map((option, index) => {
          const checked = option.value === '' ? values.length === 0 : values.includes(option.value);
          return (
            <li
              key={option.value}
              role="option"
              aria-selected={checked}
              tabIndex={open && index === active ? 0 : -1}
              onFocus={() => setActive(index)}
              onClick={() => choose(option.value)}
              onKeyDown={(event) => navigate(event, index)}
              className="hosting-technology-select__option"
            >
              <span className="hosting-technology-select__logo">
                {option.logo ? (
                  <img src={option.logo} alt="" />
                ) : (
                  <PrototypeIcon name="circle-help" />
                )}
              </span>
              <span>{option.name}</span>
              <span aria-hidden="true" className="hosting-technology-select__check">
                {checked ? '✓' : ''}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
function fold(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('fr-FR');
}
